import { useCallback, useEffect, useTransition } from "react";
import type { Product } from "../types";

interface Args {
  // From useNav()
  page: string;
  setPage: (p: string) => void;
  selProd: Product | null;
  setSelProd: (p: Product | null) => void;
  installNav: (fn: (page: string, prod?: Product | null) => void) => void;
  pendingDetailId: string | null;
  setPendingDetailId: (id: string | null) => void;
  // From useUI()
  setSideOpen: (v: boolean) => void;
  setPageLoading: (v: boolean) => void;
  // From useCheckout()
  setOrderDone: (v: boolean) => void;
  setCkStep: (v: number) => void;
  // From shell-local state
  setQty: (v: number) => void;
  // Catalog (for deep-link resolution)
  P: Product[];
}

/**
 * Owns the enriched `nav` callback (closes drawers, resets the wizard,
 * uses startTransition for the route swap) plus the `/detail/<id>`
 * deep-link resolver. Registers itself into `NavContext` via
 * `installNav` so every component reading `useNav().nav` gets this
 * behaviour for free.
 */
export function useNavState(args: Args) {
  const {
    page, setPage, selProd, setSelProd, installNav,
    pendingDetailId, setPendingDetailId,
    setSideOpen, setPageLoading,
    setOrderDone, setCkStep, setQty, P,
  } = args;

  const [, startNavTransition] = useTransition();

  const nav = useCallback((pg: string, prod: Product | null = null) => {
    if (prod) setQty(1);
    setSideOpen(false);
    setOrderDone(false);
    setCkStep(0);
    setPageLoading(true);
    startNavTransition(() => {
      setPage(pg);
      setSelProd(prod);
    });
    setTimeout(() => setPageLoading(false), 250);
  }, [setQty, setSideOpen, setOrderDone, setCkStep, setPageLoading, setPage, setSelProd]);

  // Register the enriched nav into NavContext.
  useEffect(() => { installNav(nav); }, [nav, installNav]);

  // Resolve `/detail/<id>` deep links once the catalog is available.
  useEffect(() => {
    if (!pendingDetailId) return;
    if (!P || P.length === 0) return;
    if (selProd && String(selProd.id) === pendingDetailId) {
      setPendingDetailId(null);
      return;
    }
    const needle = pendingDetailId;
    const found = (P as any[]).find(
      (p) =>
        String(p.id) === needle ||
        (p.slug && String(p.slug) === needle) ||
        (p.dbId && String(p.dbId) === needle),
    );
    if (found) {
      setSelProd(found);
      if (page !== "detail") setPage("detail");
    }
    setPendingDetailId(null);
  }, [pendingDetailId, P, selProd, page, setSelProd, setPage, setPendingDetailId]);

  return { nav };
}